import { motion } from "framer-motion";
import { ArrowRight, Leaf, Truck, RefreshCcw } from "lucide-react";
import { DemoBar } from "@/components/DemoBar";
import { CASES } from "@/data/cases";

const img = CASES.find((c) => c.slug === "toejbutik").image;

const ITEMS = [
  { name: "Uldfrakke — Sand", price: "1.499 kr" },
  { name: "Strikbluse — Råhvid", price: "649 kr" },
  { name: "Linnedbuks — Oliven", price: "549 kr" },
  { name: "Silkeskjerf — Print", price: "349 kr" },
];

export default function ToejbutikDemo() {
  return (
    <div data-testid="demo-toejbutik" className="min-h-screen bg-[#F5F1EA] text-[#1C1A17]">
      <DemoBar name="Atelier Nord" />

      <header className="flex items-center justify-between border-b border-[#1C1A17]/10 px-6 py-6 md:px-12">
        <p className="font-syne text-xl font-extrabold tracking-[0.15em]">ATELIER NORD</p>
        <nav className="hidden gap-8 text-sm uppercase tracking-[0.2em] text-[#1C1A17]/60 md:flex">
          <span className="cursor-pointer transition-colors hover:text-[#1C1A17]">Dame</span>
          <span className="cursor-pointer transition-colors hover:text-[#1C1A17]">Herre</span>
          <span className="cursor-pointer transition-colors hover:text-[#1C1A17]">Arkiv</span>
        </nav>
        <button data-testid="toejbutik-shop-button" className="rounded-full border border-[#1C1A17] px-6 py-2.5 text-sm font-semibold transition-colors duration-300 hover:bg-[#1C1A17] hover:text-[#F5F1EA]">
          Køb nu
        </button>
      </header>

      <section className="relative overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-2">
          <div className="flex flex-col justify-center px-6 py-20 md:px-12 md:py-32">
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="text-sm uppercase tracking-[0.3em] text-[#1C1A17]/50">
              Efterår / Vinter 2026
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="mt-4 font-syne text-5xl font-extrabold leading-[0.95] tracking-tighter md:text-7xl"
            >
              Tidløst tøj.<br />Ærlige materialer.
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }} className="mt-6 max-w-md text-[#1C1A17]/70">
              Kollektioner i uld, linned og silke — designet i København, produceret med omtanke i Europa.
            </motion.p>
            <motion.button
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.35 }}
              data-testid="toejbutik-collection-cta"
              className="group mt-10 flex w-fit items-center gap-2 rounded-full bg-[#1C1A17] px-8 py-4 text-sm font-semibold text-[#F5F1EA] transition-transform duration-300 hover:scale-105"
            >
              Udforsk kollektionen <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </motion.button>
          </div>
          <motion.div initial={{ opacity: 0, scale: 1.02 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1 }} className="min-h-[24rem]">
            <img src={img} alt="Kollektion hos Atelier Nord" data-testid="toejbutik-hero-image" className="h-full w-full object-cover" />
          </motion.div>
        </div>
      </section>

      <section className="px-6 py-20 md:px-12">
        <h2 className="font-syne text-3xl font-extrabold tracking-tighter md:text-5xl">Nyheder i butikken</h2>
        <div data-testid="toejbutik-products" className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {ITEMS.map((item, i) => (
            <motion.div
              key={item.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              className="group cursor-pointer"
            >
              <div className="aspect-[3/4] overflow-hidden rounded-xl bg-[#E8E1D4]">
                <img src={img} alt={item.name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
              </div>
              <h3 className="mt-4 font-syne font-bold">{item.name}</h3>
              <p className="text-sm text-[#1C1A17]/60">{item.price}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="grid grid-cols-1 gap-8 border-t border-[#1C1A17]/10 px-6 py-16 text-center sm:grid-cols-3 md:px-12">
        {[
          { icon: Leaf, text: "Bæredygtige materialer" },
          { icon: Truck, text: "Fri fragt over 500 kr" },
          { icon: RefreshCcw, text: "30 dages returret" },
        ].map(({ icon: Icon, text }) => (
          <div key={text} className="flex flex-col items-center gap-3">
            <Icon className="h-6 w-6" />
            <p className="text-sm uppercase tracking-[0.2em] text-[#1C1A17]/70">{text}</p>
          </div>
        ))}
      </section>

      <footer className="border-t border-[#1C1A17]/10 px-6 py-10 text-center text-sm text-[#1C1A17]/60 md:px-12">
        Atelier Nord · Modegaden 4 · 1307 København K · hej@ateliernord.dk
      </footer>
    </div>
  );
}
